import { Component, Signal, computed, inject, input, signal } from '@angular/core';
import { LocaleDateProvider } from '@secondaries/scenario/providers/locale-date.provider';
import { AnalystAssignment } from '@scenario/models/analyst-assignment.model';
import { PsfUser } from '@scenario/models/psf-user.model';
import { Source } from '@scenario/models/source.model';
import { dispatch } from '@store/injectables';
import { removeAnalyst } from '@store/scenario/analyst/analyst-slice';
import {
  ButtonComponent,
  ModalService,
  SegmentedControlComponent,
  SegmentedControlOptionComponent,
  TileComponent,
} from '@totalenergiescode/tds-angular';
import { AnalystFormModalComponent } from '../analyst-form-modal/analyst-form-modal.component';

@Component({
  selector: 'app-analyst-section',
  templateUrl: './analyst-section.component.html',
  styleUrl: './analyst-section.component.scss',
  imports: [ButtonComponent, SegmentedControlComponent, SegmentedControlOptionComponent, TileComponent],
})
export class AnalystSectionComponent {
  private readonly dispatch = dispatch();
  private readonly dateProvider = new LocaleDateProvider(navigator.language);
  private readonly modalService = inject(ModalService);

  public readonly source = input.required<Source>();
  public readonly analysts = input.required<AnalystAssignment[]>();
  public readonly users = input.required<PsfUser[]>();

  public readonly selectedTab = signal(0);

  public readonly sourceAnalysts: Signal<AnalystAssignment[]> = computed(() =>
    this.analysts().filter(analyst => analyst.sourceId === this.source().id),
  );

  public readonly activeAnalysts: Signal<AnalystAssignment[]> = computed(() =>
    this.sourceAnalysts().filter(analyst => !this.isFormer(analyst)),
  );

  public readonly formerAnalysts: Signal<AnalystAssignment[]> = computed(() =>
    this.sourceAnalysts().filter(analyst => this.isFormer(analyst)),
  );

  public readonly availableUsers: Signal<PsfUser[]> = computed(() => {
    const activeUserIds = new Set(this.activeAnalysts().map(analyst => analyst.userId));
    return this.users().filter(user => !activeUserIds.has(user.id));
  });

  formatDate(date: string): string {
    return this.dateProvider.convertToLocaleString(date);
  }

  fullName(analyst: AnalystAssignment): string {
    return [analyst.firstName, analyst.lastName].filter(Boolean).join(' ');
  }

  zonesLabel(analyst: AnalystAssignment): string {
    return (analyst.zones ?? []).map(zone => zone.alias).join(', ');
  }

  openCreate(): void {
    this.modalService.open({
      content: {
        component: AnalystFormModalComponent,
        inputs: {
          source: this.source(),
          users: this.availableUsers(),
        },
      },
    });
  }

  removeAnalyst(analyst: AnalystAssignment): void {
    if (confirm(`Remove ${analyst.userEmail} as analyst for ${this.source().name}?`)) {
      this.dispatch(removeAnalyst(analyst.id));
    }
  }

  private isFormer(analyst: AnalystAssignment): boolean {
    return !!analyst.endDate && !!analyst.removedByEmail;
  }
}
